import { BlocoCampos, CampoArea, CampoTexto, Grade } from '../componentes/campos';
import { ListaEditavel, ListaTextos } from '../componentes/lista-editavel';
import { SlotImagem } from '../componentes/slot-imagem';
import { CATALOGO_NORMAS, CATALOGO_PUBLICO } from '../manual-catalogos';
import { novaNorma, novoTexto } from '../manual-defaults';
import type { ItemNorma } from '../manual-types';
import type { PropsSecao } from './tipos';

export function SecaoIdentificacao({ editor }: PropsSecao) {
  const { dados, definirCampo, adicionarItem, adicionarItens, atualizarItem, removerItem, moverItem } = editor;

  return (
    <>
      <BlocoCampos titulo="Designação da máquina" descricao="Dados que identificam o equipamento na capa, no cabeçalho e no capítulo 1.">
        <Grade>
          <CampoTexto largo obrigatorio rotulo="Nome da máquina" valor={dados.nomeMaquina} placeholder="Ex.: Prensa hidráulica de buchas" aoMudar={(valor) => definirCampo('nomeMaquina', valor)} />
          <CampoTexto rotulo="Identificação / TAG" obrigatorio valor={dados.identificacao} placeholder="Ex.: PRH0311" aoMudar={(valor) => definirCampo('identificacao', valor)} />
          <CampoTexto rotulo="Modelo / tipo" valor={dados.modeloTipo} placeholder="Ex.: Hidráulica de coluna" aoMudar={(valor) => definirCampo('modeloTipo', valor)} />
          <CampoTexto rotulo="Número de série" valor={dados.numeroSerie} placeholder="N/I quando não houver placa" aoMudar={(valor) => definirCampo('numeroSerie', valor)} />
          <CampoTexto rotulo="Ano de fabricação" valor={dados.anoFabricacao} placeholder="Ex.: 1998" aoMudar={(valor) => definirCampo('anoFabricacao', valor)} />
          <CampoTexto largo obrigatorio rotulo="Código de rastreabilidade" valor={dados.codigoRastreabilidade} placeholder="Ex.: EFCJ-MTR-EMI-OFFUN-PRH0311" aoMudar={(valor) => definirCampo('codigoRastreabilidade', valor)} />
          <CampoTexto rotulo="Versão do manual" valor={dados.versao} aoMudar={(valor) => definirCampo('versao', valor)} />
          <CampoTexto rotulo="Data de emissão" tipo="date" valor={dados.dataEmissao} aoMudar={(valor) => definirCampo('dataEmissao', valor)} />
        </Grade>
      </BlocoCampos>

      <BlocoCampos titulo="Levantamento técnico" descricao="Registro da visita que originou a reconstituição do manual.">
        <Grade>
          <CampoTexto rotulo="Data do levantamento" tipo="date" valor={dados.dataLevantamento} aoMudar={(valor) => definirCampo('dataLevantamento', valor)} />
          <CampoTexto rotulo="Responsável técnico" valor={dados.responsavelLevantamento} placeholder="Nome e registro profissional" aoMudar={(valor) => definirCampo('responsavelLevantamento', valor)} />
          <CampoTexto largo rotulo="Local do levantamento" valor={dados.localLevantamento} placeholder="Unidade, setor e cidade" aoMudar={(valor) => definirCampo('localLevantamento', valor)} />
        </Grade>
      </BlocoCampos>

      <BlocoCampos titulo="Fabricante e contratante">
        <Grade>
          <CampoTexto rotulo="Fabricante original" valor={dados.fabricante} placeholder="Razão social" aoMudar={(valor) => definirCampo('fabricante', valor)} />
          <CampoTexto rotulo="Contato do fabricante" valor={dados.fabricanteContato} placeholder="Telefone, e-mail ou site" aoMudar={(valor) => definirCampo('fabricanteContato', valor)} />
          <CampoTexto rotulo="Contratante" obrigatorio valor={dados.contratante} placeholder="Razão social" aoMudar={(valor) => definirCampo('contratante', valor)} />
          <CampoTexto rotulo="Unidade" valor={dados.contratanteUnidade} placeholder="Planta ou filial" aoMudar={(valor) => definirCampo('contratanteUnidade', valor)} />
          <CampoTexto largo rotulo="Setor de instalação" valor={dados.setor} placeholder="Ex.: Oficina de manutenção" aoMudar={(valor) => definirCampo('setor', valor)} />
        </Grade>
      </BlocoCampos>

      <BlocoCampos titulo="Imagem de capa" descricao="Nenhuma imagem do manual-modelo é reaproveitada: a capa começa vazia.">
        <SlotImagem
          rotulo="Foto principal da máquina"
          imagem={dados.imagemCapa}
          altura={220}
          aoDefinir={(imagem) => definirCampo('imagemCapa', imagem)}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Materiais consultados" descricao="Documentos, registros e fontes usados na reconstituição (item 1.2).">
        <ListaTextos
          itens={dados.documentosConsultados}
          placeholder="Ex.: Diagrama elétrico do painel, revisão 02"
          rotuloAdicionar="Adicionar documento"
          vazio="Nenhum documento relacionado."
          aoAdicionar={() => adicionarItem('documentosConsultados', novoTexto())}
          aoAtualizar={(id, patch) => atualizarItem('documentosConsultados', id, patch)}
          aoRemover={(id) => removerItem('documentosConsultados', id)}
          aoMover={(id, direcao) => moverItem('documentosConsultados', id, direcao)}
          sugestoes={['Registro fotográfico do levantamento', 'Apreciação de risco NR-12', 'Diagrama elétrico', 'Entrevista com operadores', 'Placa de identificação da máquina']}
          aoEscolherSugestao={(valor) => adicionarItem('documentosConsultados', novoTexto(valor))}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Público-alvo" descricao="Profissionais a quem o manual se destina (item 1.4).">
        <ListaTextos
          itens={dados.publicoAlvo}
          placeholder="Ex.: Operador de produção"
          rotuloAdicionar="Adicionar público"
          vazio="Nenhum público definido."
          aoAdicionar={() => adicionarItem('publicoAlvo', novoTexto())}
          aoAtualizar={(id, patch) => atualizarItem('publicoAlvo', id, patch)}
          aoRemover={(id) => removerItem('publicoAlvo', id)}
          aoMover={(id, direcao) => moverItem('publicoAlvo', id, direcao)}
          sugestoes={CATALOGO_PUBLICO}
          aoEscolherSugestao={(valor) => adicionarItem('publicoAlvo', novoTexto(valor))}
        />
      </BlocoCampos>

      <BlocoCampos
        titulo="Legislação e normas"
        descricao="Referências aplicáveis na data do levantamento (item 1.5)."
        acoes={
          <button
            type="button"
            className="manual-ghost-button"
            onClick={() => adicionarItens('normas', CATALOGO_NORMAS.map((norma) => novaNorma(norma)))}
          >
            Carregar conjunto NR-12
          </button>
        }
      >
        <ListaEditavel<ItemNorma>
          itens={dados.normas}
          colunas={[
            { chave: 'codigo', rotulo: 'Documento', placeholder: 'NR-12', largura: '0.7fr' },
            { chave: 'titulo', rotulo: 'Título', placeholder: 'Segurança no trabalho em máquinas', largura: '2fr' },
            { chave: 'ano', rotulo: 'Ano', placeholder: '2022', largura: '0.5fr' },
          ]}
          rotuloAdicionar="Adicionar norma"
          vazio="Nenhuma norma relacionada."
          aoAdicionar={() => adicionarItem('normas', novaNorma())}
          aoAtualizar={(id, patch) => atualizarItem('normas', id, patch)}
          aoRemover={(id) => removerItem('normas', id)}
          aoMover={(id, direcao) => moverItem('normas', id, direcao)}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Observações do levantamento" descricao="Texto livre incorporado à descrição funcional do capítulo 3.">
        <CampoArea
          rotulo="Descrição funcional da máquina"
          obrigatorio
          linhas={5}
          valor={dados.descricao}
          placeholder="Descreva a função, o processo executado, os principais conjuntos e os sistemas de comando."
          aoMudar={(valor) => definirCampo('descricao', valor)}
        />
      </BlocoCampos>
    </>
  );
}
